import { v4 as uuid } from 'uuid';
import { Canvas, FabricImage, Point, Rect } from 'fabric';
import { BadgeTextbox } from './textbox.js';
import { BadgeQrCode } from './qr-code.js';
import { BadgeImage } from './image.js';
import { ToolName } from '../enum.js';

/**
 * EditorElementsController
 *
 * Responsible for all canvas operations and visual manipulations.
 * This class serves as the view layer and should ONLY handle canvas-related operations.
 *
 * All business logic, state management, and high-level operations should reside in
 * the BadgeEditorController, which is the parent controller for this class.
 *
 * Responsibilities:
 * - Adding/removing elements to/from the canvas
 * - Managing canvas interactions (panning, zooming, selection)
 * - Handling visual properties (backdrop, background, cursor)
 * - Emitting low-level canvas events
 *
 * @emits element-modified, element-added, selection, zoom, backdrop, image-placeholder-removed
 */
export class EditorElementsController {
    /**
     * @param {import('fabric').Canvas} canvas
     * @param {import('libs/utils/event-emitter').EventEmitter['emit']} emit
     */
    constructor(canvas, emit) {
        /** @type {Map<keyof typeof ToolName, string>} */
        this._toolToCursor = new Map([
            [ToolName.TEXT_BOX, 'text'],
            [ToolName.QR_CODE, 'crosshair'],
            [ToolName.PANNING, 'grab'],
            [ToolName.IMAGE, 'crosshair'],
            [ToolName.BACKGROUND, 'default'],
        ]);

        /** @type {{MAX: number, MIN: number}} */
        this._ZoomLimits = {
            MAX: 20,
            MIN: 0.01
        };

        /** @type {Rect|null} */
        this._backdrop = null;

        /** @type {FabricImage|null} */
        this._background = null;

        /** @type {ToolName|null} */
        this._currentTool = null;

        /** @type {import('fabric').Canvas} */
        this._canvas = new Canvas(canvas);

        /** @type {import('libs/utils/event-emitter').EventEmitter['emit']} */
        this._emit = emit;

        /** @type {Map<string, import('fabric').FabricObject>} */
        this._elements = new Map();

        /** @type {light|dark} */
        this._theme = window.$cookies.get('theme') || 'light';

        /**
         * Handles theme changes
         *
         * @type {(CustomEvent<{theme: 'light'|'dark'}) => void>} event
         */
        this._themeChangeHandler = ({ detail: { theme } }) => {
            this._theme = theme;
            this._setupBackgroundColor();
        };

        this._setupThemeObserver();
        this._setupBackgroundColor();
        this._setupCanvasEvents();
        this._addBackdrop();
        this._setupPanning();
        this._setupSelection();
        this._setupZooming();
        this.setTool('panning');
    }

    /** @type {(id: string) => import('fabric').FabricObject | undefined} */
    get(id) {
        return this._elements.get(id);
    }

    /** @type {() => string[]} */
    getIds() {
        return Array.from(this._elements.keys());
    }

    /** @type {() => import('fabric').FabricImage | null} */
    getBackground() {
        return this._background;
    }

    /** @type {() => { width: number, height: number }} */
    getBackdropDimensions() {
        return {
            width: this._backdrop.width,
            height: this._backdrop.height,
        };
    }

    /** @type {(imgElement: HTMLImageElement, id: string) => import('fabric').FabricImage} */
    addBackground(imgElement, id) {
        this._background = new FabricImage(imgElement, { top: 0, left: 0, selectable: false });
        this._background.set({
            id,
            hasBorders: false,
            hasControls: false,
            lockMovementY: true,
            lockMovementX: true
        });

        this._canvas.add(this._background);
        this._canvas.sendObjectToBack(this._background);
        this._canvas.sendObjectToBack(this._backdrop);

        return this._background;
    }

    /**
     * Add a textbox to the canvas with events and properties setup
     *
     * @param {string} text Initial text of the textbox
     * @param {BadgeEditor.Textbox} props
     * @returns {BadgeTextbox}
     */
    addTextbox(text, props) {
        const textbox = new BadgeTextbox(text, { moveCursor: 'grabbing', hoverCursor: 'pointer', ...props });
        // We remove corners because they are troublesome
        textbox.setControlsVisibility({
            bl: false,
            br: false,
            tl: false,
            tr: false,
            mtr: false,
        });
        // Adding additional properties for handling of text size during resizing
        textbox.set({
            id: props._id,

            // recalculates font size - preferably in the preview mode.
            useDynamicFontSize: props.useDynamicFontSize,

            minFontSize: props.minFontSize,
            fontSize: props.fontSize,
            maxFontSize: props.maxFontSize ?? props.fontSize,
        });

        // This is a hack so that the height corners (bottom middle and top middle) resize the box instead of scaling the text
        const handleScaling = (() => {
            let lastHeight;

            return () => {
                const { __corner: corner, height, scaleY } = textbox;
                if (corner && !['mr', 'ml'].includes(corner)) {
                    lastHeight = height * scaleY;
                }

                textbox.set({
                    height: lastHeight || height,
                    scaleY: 1,
                });

                textbox.recalculateFontSize();
            };
        })();

        textbox.once('added', () => {
            // scales font after editor canvas loads saved textbox.
            setTimeout(() => textbox.recalculateFontSize(), 0);
        });

        textbox.on('scaling', handleScaling);
        textbox.on('modified', handleScaling);
        textbox.on('editing:entered', handleScaling);
        textbox.on('changed', () => textbox.recalculateFontSize());
        textbox.on('resizing', () => textbox.recalculateFontSize());

        this._add(props._id, textbox);

        return textbox;
    }

    /**
     * Add an image to the canvas with events and properties setup
     *
     * @param {HTMLImageElement} imgElement Loaded img element
     * @param {BadgeEditor.QrCode} props
     * @returns {BadgeQrCode}
     */
    addQrCode(imgElement, props) {
        const qrcode = new BadgeQrCode(imgElement, props || {});
        qrcode.setControlsVisibility({
            ml: false,
            mt: false,
            mr: false,
            mb: false,
            mtr: false,
        });

        this._add(props._id, qrcode);

        return qrcode;
    }

    /**
     * @param {HTMLImageElement} imgElement
     * @param {BadgeEditor} props
     * @returns {BadgeImage}
     */
    addImage(imgElement, props) {
        const image = new BadgeImage(imgElement, props || {});
        image.setControlsVisibility({
            ml: false,
            mt: false,
            mr: false,
            mb: false,
            mtr: false,
        });

        this._add(props._id, image);

        return image;
    }

    /**
     * Set current tool
     *
     * @param {'panning'|'textbox'|'qrcode'|'image'|'background'} tool
     */
    setTool(tool) {
        this._currentTool = tool;
        const cursor = this._toolToCursor.get(tool);
        if (cursor) {
            this._canvas.discardActiveObject();
            this._canvas.set({
                selection: false
            });
            this._setCursor(cursor);
        } else {
            this._setCursor(this._toolToCursor.get('background'));
        }
        this.rerender();
    }

    /**
     * Set the zoom to a value between maximum and minimum. The zoom focus is the center of the canvas
     *
     * @param {number} zoom
     * @emits EditorElementsController#zoom
     */
    setZoom(zoom) {
        const validated = Math.min(Math.max(zoom, this._ZoomLimits.MIN), this._ZoomLimits.MAX);
        const { width, height } = this._canvas;

        this._canvas.zoomToPoint(new Point(width / 2, height / 2), validated);
        this._emit('zoom', validated);
    }

    /**
     * Updates the dimensions of the backdrop
     *
     * @param {number} width
     * @param {number} height
     */
    updateBackdropDimensions(width, height) {
        this._backdrop.set({
            width: width,
            height: height
        });

        this._emit('backdrop', { width, height });

        this._backdrop.setCoords();

        this._zoomToBackdrop();
    }

    /**
     * Activates selection of a given element
     *
     * @param {string} id
     */
    activate(id) {
        requestAnimationFrame(() => {
            this.setTool('panning');

            const element = this.get(id);
            this._canvas.setActiveObject(element, event);
            this.rerender();
            this._emit('selection', [element]);
        });
    }

    /** @type {() => string} */
    generateId() {
        return uuid();
    }

    /**
     * Calculates static width and height that the canvas should have
     */
    recalculateDimensions() {
        const previousCenter = this._canvas.getCenterPoint();

        // We use CSS to fill the entire available space
        this._canvas.setDimensions({ width: '100%', height: '100%' });
        // Read what those numbers are
        const { width, height } = this._canvas.getElement().getBoundingClientRect();
        // And set them statically
        this._canvas.setDimensions({ width, height });

        const newCenter = this._canvas.getCenterPoint();

        // We make sure that our center point remains in the same place
        this._canvas.relativePan(newCenter.subtract(previousCenter));
    }

    rerender() {
        this._canvas.renderAll();
    }

    remove(id) {
        const element = this.get(id);
        if (element) {
            this._canvas.remove(element);
            this._elements.delete(id);
        }
    }

    removeBackground() {
        this._canvas.remove(this._background);
        this._background = null;
    }

    /** @type {() => string} */
    asSVG() {
        const { width, height } = this._backdrop;
        return this._canvas.toSVG({
            viewBox: {
                x: 0,
                y: 0,
                width,
                height,
            },
            width,
            height,
        });
    }

    /** @type {() => void} */
    dispose() {
        window.removeEventListener('theme-change', this._themeChangeHandler);
    }

    /**
     * Add an object to the editor
     *
     * @param {string | undefined} id
     * @param {import('fabric').FabricObject} element
     * @returns {string}
     */
    _add(id, element) {
        if (id === undefined) {
            id = this.generateId();
        }

        this._elements.set(id, element);
        this._canvas.add(element);

        return id;
    }

    /** @type {() => import('fabric').Rect} */
    _addBackdrop() {
        this._backdrop = new Rect({
            id: 'backdrop',
            fill: '#ffffff',
            top: 0,
            left: 0,
            width: 100,
            height: 100,
            hasBorders: false,
            hasControls: false,
            hoverCursor: 'normal',
            selectable: false,
            lockMovementY: true,
            lockMovementX: true
        });
        this._backdrop.on('mousedown', () => this._emit('selection', 'background'));

        this._canvas.add(this._backdrop);

        return this._backdrop;
    }

    /**
     * Convenience function to set current cursor
     *
     * @param {string} cursor
     */
    _setCursor(cursor) {
        const types = ['defaultCursor', 'hoverCursor', 'moveCursor', 'freeDrawingCursor', 'notAllowedCursor'];
        for (const type of types) {
            this._canvas[type] = cursor;
        }
    }

    _setupCanvasEvents() {
        this._canvas.on('object:modified', (event) => this._emit('element-modified', event));

        this._canvas.on('mouse:down', (event) => {
            const { x: left, y: top } = this._canvas.getScenePoint(event.e);
            const id = this.generateId();

            switch (this._currentTool) {
                case ToolName.TEXT_BOX:
                    this._emit('element-added', { id, type: ToolName.TEXT_BOX, left, top });
                    break;
                case ToolName.QR_CODE:
                    this._emit('element-added', { id, type: ToolName.QR_CODE, left, top });
                    break;
                case ToolName.IMAGE:
                    this._emit('element-added', { id, type: ToolName.IMAGE, left, top });
                    break;
                default:
                    return;
            }
        });
    }

    /** @type {() => void} */
    _setupBackgroundColor() {
        this._canvas.backgroundColor = getComputedStyle(document.documentElement)
            .getPropertyValue(this._theme === 'dark' ? '--base-100' : '--base-300')
            .trim();
        this.rerender();
    }

    _setupThemeObserver() {
        window.addEventListener('theme-changed', this._themeChangeHandler);
    }

    /**
     * Sets up mouse panning tool interaction
     */
    _setupPanning() {
        this._canvas.on('mouse:down', (downEvent) => {
            const target = downEvent.target;
            if (this._currentTool === 'panning' && [undefined, this._backdrop, this._background].includes(target)) {
                const { screenX, screenY } = downEvent.e;
                let lastPoint = new Point(screenX, screenY);
                this._setCursor('grabbing');

                const unsubMove = this._canvas.on('mouse:move', (moveEvent) => {
                    const { screenX, screenY } = moveEvent.e;
                    const point = new Point(screenX, screenY);
                    this._canvas.relativePan(point.subtract(lastPoint));
                    lastPoint = point;
                });

                const unsubEnd = this._canvas.on('mouse:up', (upEvent) => {
                    const { screenX, screenY } = upEvent.e;
                    const point = new Point(screenX, screenY);
                    this._canvas.relativePan(point.subtract(lastPoint));
                    unsubEnd();
                    unsubMove();

                    this._setCursor('grab');
                });
            }
        });
    }

    /**
     * Sets up selection interactions
     *
     * @emits EditorElementsController#selection
     */
    _setupSelection() {
        const handleSelection = (event) => {
            const selected = event.selected || [];
            const deselected = event.deselected || [];

            const emit = () => this._emit('selection', selected);
            emit();

            for (const item of selected) {
                item.on('modified', emit);
                item.on('moving', emit);
            }

            for (const item of deselected) {
                // Removal of placeholder images when deselected
                if (item.isImage && item.id === 'placeholder') {
                    const id = item._id;
                    this.remove(id);
                    this._emit('image-placeholder-removed', id);
                }
            }
        };

        this._canvas.on({
            'selection:created': handleSelection,
            'selection:updated': handleSelection,
            'selection:cleared': handleSelection,
        });
    }

    /**
     * Sets up mouse wheel zoom interaction
     *
     * @emits EditorElementsController#zoom
     */
    _setupZooming() {
        this._canvas.on('mouse:wheel', (event) => {
            const wheelEvent = event.e;
            const max = this._ZoomLimits.MAX;
            const min = this._ZoomLimits.MIN;
            let zoom = this._canvas.getZoom();
            zoom *= 0.999 ** wheelEvent.deltaY;

            if (zoom > max) zoom = max;
            if (zoom < min) zoom = min;

            this._canvas.zoomToPoint(event.viewportPoint, zoom);
            this._canvas.setZoom(zoom);

            this._emit('zoom', zoom);

            wheelEvent.preventDefault();
            wheelEvent.stopPropagation();
        });
    }

    /**
     * Pan and zoom to the backdrop so its visible in its entirety
     *
     * @emits EditorElementsController#zoom
     */
    _zoomToBackdrop() {
        // Always start from an untransformed canvas
        this._canvas.viewportTransform = [1, 0, 0, 1, 0, 0];
        const { width, height } = this._backdrop;

        const contentWidth = width + 60;
        const contentHeight = height + 60;

        const contentCenter = new Point(contentWidth / 2, contentHeight / 2);
        const canvasCenter = new Point(this._canvas.width / 2, this._canvas.height / 2);

        const difference = contentCenter.subtract(canvasCenter).subtract({ x: 30, y: 30 });
        this._canvas.absolutePan(difference);

        const zoom = Math.min(this._canvas.width / contentWidth, this._canvas.height / contentHeight);
        this._canvas.zoomToPoint(canvasCenter, zoom);

        this._emit('zoom', zoom);
        this.rerender();
    }
}
